import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService } from '@angular-workspace/core';

/**
 * Injection token for sandbox API base URL
 */
export const SANDBOX_BASE_URL = new InjectionToken<string>('SANDBOX_BASE_URL');

export interface JupyterLiteSessionResponse {
  launchUrl: string;
}

export interface ListBookingsParams {
  statuses?: string[];
  limit?: number;
  offset?: number;
}

export interface ListBookingsResult {
  bookings: unknown[];
  total: number;
  limit: number;
  offset: number;
  /** True when `total` comes from the API or a short (non-full) page — not a page-1 guess. */
  totalIsExact: boolean;
}

/** Normalizes bookings list API payload and resolves total count for paginator. */
export function parseListBookingsResponse(
  response: unknown,
  limit: number,
  offset: number,
): ListBookingsResult {
  const root = response as Record<string, unknown> | null | undefined;
  const bookings = extractBookingsArray(root);
  const data =
    root?.['data'] && typeof root['data'] === 'object'
      ? (root['data'] as Record<string, unknown>)
      : undefined;

  const totalCandidate =
    readNumber(root?.['total']) ??
    readNumber(root?.['totalCount']) ??
    readNumber(root?.['total_count']) ??
    readNumber(root?.['count']) ??
    readNumber(data?.['total']) ??
    readNumber(data?.['totalCount']) ??
    readNumber(data?.['total_count']) ??
    readNumber((root?.['pagination'] as Record<string, unknown> | undefined)?.['total']) ??
    readNumber((data?.['pagination'] as Record<string, unknown> | undefined)?.['total']) ??
    readNumber(
      (root?.['paginationInfo'] as Record<string, unknown> | undefined)?.['totalCount'],
    ) ??
    readNumber((root?.['meta'] as Record<string, unknown> | undefined)?.['total']) ??
    readNumber((data?.['meta'] as Record<string, unknown> | undefined)?.['total']);

  let total: number;
  let totalIsExact: boolean;
  if (totalCandidate !== null) {
    total = totalCandidate;
    totalIsExact = true;
  } else if (bookings.length < limit) {
    total = offset + bookings.length;
    totalIsExact = true;
  } else if (offset === 0) {
    // First page full with no API total — allow "next" only; do not treat as final count.
    total = offset + bookings.length + 1;
    totalIsExact = false;
  } else {
    // Later full pages without API total — minimum loaded so far (refined via peek in UI).
    total = offset + bookings.length;
    totalIsExact = false;
  }

  return { bookings, total, limit, offset, totalIsExact };
}

function extractBookingsArray(root: Record<string, unknown> | null | undefined): unknown[] {
  if (!root) return [];
  if (Array.isArray(root['bookings'])) return root['bookings'];
  const data = root['data'];
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)['bookings'])) {
    return (data as Record<string, unknown>)['bookings'] as unknown[];
  }
  if (Array.isArray(data)) return data;
  if (Array.isArray(root['result'])) return root['result'];
  if (Array.isArray(root)) return root as unknown[];
  return [];
}

function readNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Sandbox API Service for handling sandbox-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class SandboxApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(SANDBOX_BASE_URL) private sandboxBaseUrl: string,
  ) {}

  /**
   * Create a JupyterLite browser session (sets session cookie via Set-Cookie).
   * Must be called before opening the JupyterLite lab URL.
   */
  async createJupyterLiteSession(options: {
    accessToken: string;
    baseUrl?: string;
  }): Promise<JupyterLiteSessionResponse> {
    const token = this.normalizeAccessToken(options.accessToken);
    if (!token) {
      throw new Error('Missing access token for JupyterLite session');
    }
    if (!this.isLikelyJwtAccessToken(token)) {
      throw new Error('Invalid access token for JupyterLite session');
    }

    const base = (options.baseUrl ?? this.sandboxBaseUrl).trim();
    const normalizedBase = base.endsWith('/') ? base : `${base}/`;
    const url = `${normalizedBase}jupyterlite/session`;

    try {
      console.log('SandboxApiService: Calling createJupyterLiteSession API with URL:', url);

      const responseBody = await this.networkService.post_api(
        url,
        null,
        { Authorization: `Bearer ${token}` },
        false, // isToasterRequired
        false, // isLoaderRequired
        true, // withCredentials — same as fetch credentials: "include" (send/receive session cookie)
      );

      const launchUrl = this.extractJupyterLiteLaunchUrl(responseBody);
      if (!launchUrl) {
        throw new Error('JupyterLite session response did not include launchUrl');
      }

      console.log('SandboxApiService: createJupyterLiteSession API response received:', {
        launchUrl,
      });
      return { launchUrl };
    } catch (error: unknown) {
      console.error('Error creating JupyterLite session:', error);
      const detail = this.extractJupyterLiteSessionErrorDetail(error);
      throw new Error(detail);
    }
  }

  private extractJupyterLiteSessionErrorDetail(error: unknown): string {
    if (error && typeof error === 'object') {
      const err = error as { error?: unknown; message?: string; status?: number };
      const body = err.error;
      if (body && typeof body === 'object') {
        const record = body as Record<string, unknown>;
        const message = record['detail'] ?? record['title'];
        if (typeof message === 'string' && message.trim()) {
          return message.trim();
        }
      }
      if (typeof err.message === 'string' && err.message.trim()) {
        return err.message.trim();
      }
      if (typeof err.status === 'number') {
        return `HTTP ${err.status}`;
      }
    }
    return 'Failed to create JupyterLite session';
  }

  private normalizeAccessToken(raw: string | null | undefined): string {
    return String(raw ?? '')
      .trim()
      .replace(/^Bearer\s+/i, '');
  }

  private isLikelyJwtAccessToken(token: string): boolean {
    if (!token || /^https?:\/\//i.test(token)) {
      return false;
    }
    const parts = token.split('.');
    return parts.length >= 2 && parts.every((part) => part.length > 0);
  }

  private extractJupyterLiteLaunchUrl(response: unknown): string {
    if (!response || typeof response !== 'object') {
      return '';
    }
    const root = response as Record<string, unknown>;
    const direct = root['launchUrl'] ?? root['launch_url'];
    if (typeof direct === 'string' && direct.trim()) {
      return direct.trim();
    }
    for (const key of ['data', 'result'] as const) {
      const nested = root[key];
      if (nested && typeof nested === 'object') {
        const nestedRecord = nested as Record<string, unknown>;
        const nestedUrl = nestedRecord['launchUrl'] ?? nestedRecord['launch_url'];
        if (typeof nestedUrl === 'string' && nestedUrl.trim()) {
          return nestedUrl.trim();
        }
      }
    }
    return '';
  }

  /**
   * List all notebooks
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with list of notebooks
   */
  async listNotebooks(baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/list`;
      console.log('SandboxApiService: Calling listNotebooks API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listNotebooks API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing notebooks:', error);
      throw error;
    }
  }

  /**
   * List notebooks from an absolute URL.
   * Used when a specific environment requires a one-off endpoint.
   * @param url Fully qualified notebooks endpoint URL
   * @returns Promise with list of notebooks
   */
  async listNotebooksFromUrl(url: string): Promise<any> {
    try {
      console.log('SandboxApiService: Calling listNotebooksFromUrl API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listNotebooksFromUrl API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing notebooks from absolute URL:', error);
      throw error;
    }
  }

  /**
   * List available GPU instance types
   * Calls: <sandboxGpu Url>/v1/notebook/gpu-instance-types
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with list of GPU instance types
   */
  async listGpuInstances(baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      // Derive the origin from the configured sandbox base URL and build the final GPU URL.
      // Required final endpoint (as per backend): https://v2.dev.sandbox.iudx.io/api//v1/notebook/gpu-instance-types
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        // Fallback: if base is not a full URL, use it as-is
        origin = base.replace(/\/+$/, '');
      }

      const url = `${origin}/api//v1/notebook/gpu-instance-types`;
      console.log('SandboxApiService: Calling listGpuInstances API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listGpuInstances API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing GPU instances:', error);
      throw error;
    }
  }

  /**
   * List notebook categories
   * Calls: <sandbox origin>/api//v1/categories
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with list of categories
   */
  async listCategories(baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const url = `${origin}/api//v1/categories`;
      console.log('SandboxApiService: Calling listCategories API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listCategories API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing categories:', error);
      throw error;
    }
  }

  /**
   * List calendar slot availability for a given category and month.
   * Calls: <sandbox origin>/api//v1/slots/calendar?category=<category>&month=<YYYY-MM>
   * @param category Category ID selected by the user
   * @param month Month in YYYY-MM format
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with calendar slot availability data
   */
  async listCalendarSlots(category: string, month: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const url = `${origin}/api//v1/slots/calendar?category=${encodeURIComponent(category)}&month=${month}`;
      console.log('SandboxApiService: Calling listCalendarSlots API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log('SandboxApiService: listCalendarSlots API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing calendar slots:', error);
      throw error;
    }
  }

  /**
   * List bookings with optional status filter and offset pagination.
   * Calls: <sandbox origin>/api//v1/bookings?limit=&offset=[&status=]
   */
  async listBookings(
    params: ListBookingsParams = {},
    baseUrl?: string,
  ): Promise<ListBookingsResult> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const limit = params.limit ?? 10;
      const offset = params.offset ?? 0;
      const query = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (params.statuses && params.statuses.length > 0) {
        query.set('status', params.statuses.join(','));
      }

      const url = `${origin}/api//v1/bookings?${query.toString()}`;
      console.log('SandboxApiService: Calling listBookings API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log('SandboxApiService: listBookings API response received:', response);
      return parseListBookingsResponse(response, limit, offset);
    } catch (error) {
      console.error('Error listing bookings:', error);
      throw error;
    }
  }

  /**
   * List available slots for a given category and date.
   * Calls: <sandbox origin>/api//v1/slots/available?category=<category>&date=<YYYY-MM-DD>
   * @param category Category key selected by the user (e.g. cpu_basic)
   * @param date Date in YYYY-MM-DD format
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with available slots data
   */
  async listAvailableSlots(category: string, date: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const url = `${origin}/api//v1/slots/available?category=${encodeURIComponent(category)}&date=${date}`;
      console.log('SandboxApiService: Calling listAvailableSlots API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log('SandboxApiService: listAvailableSlots API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing available slots:', error);
      throw error;
    }
  }

  /**
   * Create a new booking (POST /api//v1/bookings)
   * @param payload { category, notebookName, slotDate, slotKeys }
   * @param baseUrl Optional base URL
   */
  async createBooking(
    payload: { category: string; notebookName: string; slotDate: string; slotKeys: string[] },
    baseUrl?: string,
  ): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const url = `${origin}/api//v1/bookings`;
      console.log('SandboxApiService: Calling createBooking API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.post_api(url, payload, undefined, false);

      console.log('SandboxApiService: createBooking API response received:', response);
      return response;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  }

  /**
   * Extend an existing booking.
   * Calls: <sandbox origin>/api//v1/bookings/<booking-id>/extend
   * @param bookingId Booking identifier from list bookings response
   * @param baseUrl Optional base URL
   */
  async extendBooking(bookingId: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const id = encodeURIComponent((bookingId ?? '').trim());
      const url = `${origin}/api//v1/bookings/${id}/extend`;
      console.log('SandboxApiService: Calling extendBooking API with URL:', url);

      const response = await this.networkService.patch_api(url, {}, undefined);

      console.log('SandboxApiService: extendBooking API response received:', response);
      return response;
    } catch (error) {
      console.error('Error extending booking:', error);
      throw error;
    }
  }

  /**
   * Terminate an existing booking.
   * Calls: <sandbox origin>/api//v1/bookings/<booking-id>/terminate
   * @param bookingId Booking identifier from list bookings response
   * @param baseUrl Optional base URL
   */
  async terminateBooking(bookingId: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const id = encodeURIComponent((bookingId ?? '').trim());
      const url = `${origin}/api//v1/bookings/${id}/terminate`;
      console.log('SandboxApiService: Calling terminateBooking API with URL:', url);

      const response = await this.networkService.patch_api(url, {}, undefined);

      console.log('SandboxApiService: terminateBooking API response received:', response);
      return response;
    } catch (error) {
      console.error('Error terminating booking:', error);
      throw error;
    }
  }

  /**
   * Cancel an existing booking.
   * Calls: <sandbox origin>/api//v1/bookings/<booking-id>/cancel
   * @param bookingId Booking identifier from list bookings response
   * @param baseUrl Optional base URL
   */
  async cancelBooking(bookingId: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const id = encodeURIComponent((bookingId ?? '').trim());
      const url = `${origin}/api//v1/bookings/${id}/cancel`;
      console.log('SandboxApiService: Calling cancelBooking API with URL:', url);

      const response = await this.networkService.patch_api(url, {}, undefined);

      console.log('SandboxApiService: cancelBooking API response received:', response);
      return response;
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  }

  /**
   * Reset a ready booking so the user can book again.
   * Calls: <sandbox origin>/api//v1/bookings/<booking-id>/reset
   */
  async resetBooking(bookingId: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const id = encodeURIComponent((bookingId ?? '').trim());
      const url = `${origin}/api//v1/bookings/${id}/reset`;
      console.log('SandboxApiService: Calling resetBooking API with URL:', url);

      const response = await this.networkService.patch_api(url, {}, undefined);

      console.log('SandboxApiService: resetBooking API response received:', response);
      return response;
    } catch (error) {
      console.error('Error resetting booking:', error);
      throw error;
    }
  }

  /**
   * Create a notebook token session before opening a CPU/GPU notebook.
   * Calls: <sandbox origin>/api//v1/bookings/<booking-id>/notebook-token-session
   * @param bookingId Booking identifier from list bookings response
   * @param baseUrl Optional base URL
   */
  async createNotebookTokenSession(bookingId: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        origin = base.replace(/\/+$/, '');
      }

      const id = encodeURIComponent((bookingId ?? '').trim());
      const url = `${origin}/api//v1/bookings/${id}/notebook-token-session`;
      console.log('SandboxApiService: Calling createNotebookTokenSession API with URL:', url);

      const response = await this.networkService.post_api(
        url,
        null,
        { Accept: 'application/json' },
        false,
        true,
      );

      console.log('SandboxApiService: createNotebookTokenSession API response received:', response);
      return response;
    } catch (error) {
      console.error('Error creating notebook token session:', error);
      throw error;
    }
  }

  /**
   * Create a new notebook (uses v1 path)
   * @param payload The request payload containing notebook data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the created notebook response
   */
  async createNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      // Use the configured base URL (e.g. .../sandbox-api/v1/) as-is.
      // This preserves the required path segment and avoids dropping it to origin+`/api/v1`.
      const normalizedBase = base.replace(/\/+$/, '');
      const url = `${normalizedBase}/notebook/create`;
      console.log('SandboxApiService: Calling createNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined,
        false,
      );

      console.log('SandboxApiService: createNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error creating notebook:', error);
      throw error;
    }
  }

  /**
   * Stop a notebook
   * @param payload The request payload containing notebook stop data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the stop notebook response
   */
  async stopNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/stop`;
      console.log('SandboxApiService: Calling stopNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.patch_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('SandboxApiService: stopNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error stopping notebook:', error);
      throw error;
    }
  }

  /**
   * Start a notebook
   * @param payload The request payload containing notebook start data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the start notebook response
   */
  async startNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/start`;
      console.log('SandboxApiService: Calling startNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.patch_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('SandboxApiService: startNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error starting notebook:', error);
      throw error;
    }
  }

  /**
   * Delete a notebook
   * @param payload The request payload containing notebook delete data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the delete notebook response
   */
  async deleteNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/delete`;
      console.log('SandboxApiService: Calling deleteNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.delete_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('SandboxApiService: deleteNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error deleting notebook:', error);
      throw error;
    }
  }

  /**
   * Check notebook status
   * @param statusIdentifier The status identifier sent from the component (e.g., notebook ID)
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the notebook status response
   */
  async notebookStatusCheck(statusIdentifier: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/status/${statusIdentifier}`;
      console.log('SandboxApiService: Calling notebookStatusCheck API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: notebookStatusCheck API response received:', response);
      return response;
    } catch (error) {
      console.error('Error checking notebook status:', error);
      throw error;
    }
  }

  /**
   * Check if a notebook exists
   * @param identifier The identifier sent from the component (e.g., notebook ID)
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the notebook existence check response
   */
  async checkNotebookExists(identifier: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/check-exists/${identifier}`;
      console.log('SandboxApiService: Calling checkNotebookExists API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: checkNotebookExists API response received:', response);
      return response;
    } catch (error) {
      console.error('Error checking notebook existence:', error);
      throw error;
    }
  }

  /**
   * Create a Kubeflow profile
   * @param payload The request payload containing profile data (sent from component)
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the created profile response
   */
  async createKubeFlow(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}profile/create`;
      console.log('SandboxApiService: Calling createKubeFlow API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined,
        false,
      );

      console.log('SandboxApiService: createKubeFlow API response received:', response);
      return response;
    } catch (error) {
      console.error('Error creating Kubeflow profile:', error);
      throw error;
    }
  }
}
